import type { CSSProperties } from 'react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import ReactSelect from 'react-select';
import { Button, useBreakpointValue } from '@chakra-ui/react';
import { toast } from 'react-toastify';
import ESignaturePad, { ESignaturePadHandle } from 'components/signature/ESignaturePad';
import logo from 'utils/assets/img/logo.png';
import { Employee } from 'utils/interfaces';
import PrestazioneSection from './PrestazioneSection';
import { ReportFormState, SERVICE_RATING_OPTIONS } from '../types';

export interface InterventionReportFormHandle {
  submit: () => void;
}

interface InterventionReportFormProps {
  reportNumberFormatted: string;
  reportDate: string;
  taskTitle: string;
  compiledByLabel: string;
  employeeSignatureUrl: string | null;
  allEmployees: Employee[];
  initialData: ReportFormState;
  onSubmit: (data: ReportFormState, customerSignatureDataUrl: string | null) => void;
  isSubmitting: boolean;
  submitLabel: string;
  onCancel?: () => void;
  // The customer signs once, in person, when the report is first compiled. When an admin later
  // edits the report, the customer isn't there to re-sign, so the captured signature must be
  // carried over as-is rather than left re-drawable.
  readOnlyCustomerSignature?: boolean;
  // Only true from the admin report-detail edit page — lets the admin correct the report date after
  // the fact, while employees (compiling or viewing) always see it as plain, non-editable text.
  canEditReportDate?: boolean;
  // When the caller renders its own Annulla/Salva buttons elsewhere (e.g. ReportDetailPage's top
  // header) and drives submission via the imperative `submit()` handle instead, this suppresses the
  // form's own bottom footer buttons so they don't appear twice.
  hideActions?: boolean;
}

const sectionHeaderStyle: CSSProperties = {
  fontSize: '13px',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  padding: '3px 6px',
  background: '#eef2f7',
  borderBottom: '1px solid #94a3b8',
};

const fieldLabelStyle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: '#64748b',
  display: 'block',
  marginBottom: '1px',
};

const fieldInputStyle: CSSProperties = {
  width: '100%',
  minWidth: 0,
  fontSize: '14px',
  border: 'none',
  borderBottom: '1px solid #94a3b8',
  background: 'transparent',
  padding: '2px 2px',
  boxSizing: 'border-box',
};

const InterventionReportForm = forwardRef<InterventionReportFormHandle, InterventionReportFormProps>(function InterventionReportForm({
  reportNumberFormatted,
  reportDate,
  taskTitle,
  compiledByLabel,
  employeeSignatureUrl,
  allEmployees,
  initialData,
  onSubmit,
  isSubmitting,
  submitLabel,
  onCancel,
  readOnlyCustomerSignature = false,
  canEditReportDate = false,
  hideActions = false,
}, ref) {
  const [data, setData] = useState<ReportFormState>(initialData);
  const [hasCustomerSignature, setHasCustomerSignature] = useState(Boolean(initialData.customerSignatureDataUrl));
  const customerSignatureRef = useRef<ESignaturePadHandle>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const isMobile = Boolean(useBreakpointValue({ base: true, md: false }));

  const field = <K extends keyof ReportFormState>(key: K, value: ReportFormState[K]) => {
    setData(d => ({ ...d, [key]: value }));
  };

  const toggleOperator = (employeeId: number) => {
    setData(d => ({
      ...d,
      operators: d.operators.map(o => (o.employeeId === employeeId ? { ...o, present: !o.present } : o)),
    }));
  };

  const removeOperator = (employeeId: number) => {
    setData(d => ({ ...d, operators: d.operators.filter(o => o.employeeId !== employeeId) }));
  };

  const addOperator = (employeeId: number) => {
    const emp = allEmployees.find(e => e.id === employeeId);
    if (!emp) return;
    setData(d => ({
      ...d,
      operators: [...d.operators, { employeeId: emp.id, firstName: emp.firstName, lastName: emp.lastName, present: true }],
    }));
  };

  const addAttendee = () => {
    setData(d => ({ ...d, attendees: [...d.attendees, { fullName: '', present: true, note: '' }] }));
  };

  const updateAttendee = (index: number, patch: Partial<ReportFormState['attendees'][number]>) => {
    setData(d => ({
      ...d,
      attendees: d.attendees.map((a, i) => (i === index ? { ...a, ...patch } : a)),
    }));
  };

  const removeAttendee = (index: number) => {
    setData(d => ({ ...d, attendees: d.attendees.filter((_, i) => i !== index) }));
  };

  const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024;

  const addAttachments = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const accepted: ReportFormState['attachments'] = [];
    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: solo immagini PNG o JPG sono ammesse`);
        return;
      }
      if (file.size > MAX_ATTACHMENT_SIZE) {
        toast.error(`${file.name}: dimensione massima 50MB`);
        return;
      }
      accepted.push({
        id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        fileName: file.name,
        previewUrl: URL.createObjectURL(file),
        file,
      });
    });
    if (accepted.length > 0) {
      setData(d => ({ ...d, attachments: [...d.attachments, ...accepted] }));
    }
  };

  // Only new, not-yet-uploaded attachments (string id) can be removed here — an attachment already
  // persisted from a previous save (numeric id) has no delete endpoint, by design (see plan/CLAUDE
  // context: not requested, would need its own S3 cleanup endpoint).
  const removeAttachment = (id: string | number) => {
    setData(d => ({ ...d, attachments: d.attachments.filter(a => a.id !== id) }));
  };

  const toggleVehicle = (id: number) => {
    setData(d => ({
      ...d,
      vehicles: d.vehicles.map(v => (v.id === id ? { ...v, selected: !v.selected } : v)),
    }));
  };

  const availableExtraEmployees = allEmployees.filter(e => !data.operators.some(o => o.employeeId === e.id));

  const handleSubmit = () => {
    const customerSignatureDataUrl = readOnlyCustomerSignature
      ? data.customerSignatureDataUrl
      : customerSignatureRef.current?.getSignatureDataUrl() ?? data.customerSignatureDataUrl;

    // Only enforced while compiling (the pad is editable): an admin editing an existing report
    // gets the original signature carried over read-only, so there's nothing new to validate.
    if (!readOnlyCustomerSignature && !customerSignatureDataUrl) {
      toast.error('La firma del cliente è obbligatoria per inviare il rapporto');
      return;
    }
    if (!readOnlyCustomerSignature && !data.customerSignerName.trim()) {
      toast.error('Il nome del firmatario è obbligatorio per inviare il rapporto');
      return;
    }

    onSubmit(data, customerSignatureDataUrl);
  };

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif", color: '#1a2942', border: '1px solid #94a3b8', background: 'white', width: '100%', boxSizing: 'border-box', boxShadow: '0 1px 6px rgba(26, 41, 66, 0.15)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 10px', borderBottom: '1px solid #94a3b8' }}>
        <img src={logo} alt="EGR SRL" height="22px" />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid #94a3b8' }}>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center', padding: '4px 6px', background: '#37492e', color: '#ffffff', fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Recupero Rifiuti
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center', padding: '4px 6px', background: '#9ace38', color: '#1a2942', fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Noleggio WC e Cassoni
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center', padding: '4px 6px', background: '#5ea9f3', color: '#ffffff', fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Autospurgo
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '1px solid #94a3b8', fontSize: '14px', fontWeight: 700 }}>
        <span>FOGLIO DI LAVORO N. {reportNumberFormatted}</span>
        <span>
          DATA{' '}
          {canEditReportDate ? (
            <input
              type="date"
              value={data.reportDate}
              onChange={e => field('reportDate', e.target.value)}
              style={{
                fontSize: '14px', fontWeight: 700, border: 'none', borderBottom: '1px solid #94a3b8',
                background: 'transparent', color: '#1a2942', fontFamily: 'inherit',
              }}
            />
          ) : reportDate}
        </span>
      </div>
      <div style={{ padding: '2px 10px', fontSize: '12px', color: '#64748b' }}>Intervento: {taskTitle}</div>

      {/* Cliente */}
      <div>
        <div style={sectionHeaderStyle}>Cliente</div>
        <div style={{ padding: '4px 10px' }}>
          <label style={fieldLabelStyle}>Cliente</label>
          <input style={fieldInputStyle} value={data.clientName} onChange={e => field('clientName', e.target.value)} />
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', marginTop: '4px' }}>
            <div style={{ flex: isMobile ? undefined : '0 0 calc(45% - 5px)', minWidth: 0 }}>
              <label style={fieldLabelStyle}>Comune</label>
              <input style={fieldInputStyle} value={data.clientCity} onChange={e => field('clientCity', e.target.value)} />
            </div>
            <div style={{ flex: isMobile ? undefined : '0 0 calc(55% - 5px)', minWidth: 0 }}>
              <label style={fieldLabelStyle}>Indirizzo</label>
              <input style={fieldInputStyle} value={data.clientAddress} onChange={e => field('clientAddress', e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', marginTop: '4px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={fieldLabelStyle}>C.F. / P.IVA</label>
              <input
                style={fieldInputStyle}
                value={data.clientTaxCode || data.clientVatNumber}
                onChange={e => field('clientTaxCode', e.target.value)}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={fieldLabelStyle}>Tel.</label>
              <input style={fieldInputStyle} value={data.clientPhone} onChange={e => field('clientPhone', e.target.value)} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={fieldLabelStyle}>Mail</label>
              <input style={fieldInputStyle} value={data.clientEmail} onChange={e => field('clientEmail', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Prestazione */}
      <PrestazioneSection value={data.prestazione} onChange={next => field('prestazione', next)} mobileLayout={isMobile} />

      {/* Allegati */}
      <div>
        <div style={sectionHeaderStyle}>Allegati</div>
        <div style={{ padding: '6px 10px' }}>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={e => {
              addAttachments(e.target.files);
              e.target.value = '';
            }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={e => {
              addAttachments(e.target.files);
              e.target.value = '';
            }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: data.attachments.length > 0 ? '8px' : 0 }}>
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              style={{ fontSize: '13px', border: '1px solid #94a3b8', background: 'white', cursor: 'pointer', padding: '4px 10px' }}
            >
              📷 Scatta foto
            </button>
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              style={{ fontSize: '13px', border: '1px solid #94a3b8', background: 'white', cursor: 'pointer', padding: '4px 10px' }}
            >
              🖼️ Carica da galleria
            </button>
          </div>
          {data.attachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {data.attachments.map(att => (
                <div key={att.id} style={{ position: 'relative', width: '70px', height: '70px' }}>
                  <a href={att.previewUrl} target="_blank" rel="noreferrer">
                    <img
                      src={att.previewUrl}
                      alt={att.fileName}
                      style={{ width: '70px', height: '70px', objectFit: 'cover', border: '1px solid #94a3b8', display: 'block' }}
                    />
                  </a>
                  {typeof att.id === 'string' && (
                    <button
                      type="button"
                      onClick={() => removeAttachment(att.id)}
                      title="Rimuovi"
                      style={{
                        position: 'absolute', top: '-6px', right: '-6px', width: '18px', height: '18px',
                        borderRadius: '50%', border: '1px solid #dc2626', background: 'white', color: '#dc2626',
                        cursor: 'pointer', fontSize: '11px', lineHeight: '16px', padding: 0,
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Note */}
      <div>
        <div style={sectionHeaderStyle}>Note</div>
        <textarea
          value={data.notes}
          onChange={e => field('notes', e.target.value)}
          rows={3}
          style={{ width: '100%', fontSize: '14px', border: 'none', padding: '6px 10px', boxSizing: 'border-box', resize: 'vertical', minHeight: '50px' }}
        />
      </div>

      {/* Operatore / Presenti */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', borderBottom: '1px solid #94a3b8' }}>
        <div style={{ flex: 1, minWidth: 0, borderRight: isMobile ? 'none' : '1px solid #94a3b8', borderBottom: isMobile ? '1px solid #94a3b8' : 'none' }}>
          <div style={sectionHeaderStyle}>Operatore</div>
          <div style={{ padding: '4px 8px' }}>
            {data.operators.map(op => (
              <div key={op.employeeId} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', padding: '1px 0' }}>
                <input type="checkbox" checked={op.present} onChange={() => toggleOperator(op.employeeId)} style={{ width: '11px', height: '11px', flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{op.firstName} {op.lastName}</span>
                <button type="button" onClick={() => removeOperator(op.employeeId)} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '13px', flexShrink: 0 }}>✕</button>
              </div>
            ))}
            <div style={{ marginTop: '4px', fontSize: '13px' }}>
              <ReactSelect
                placeholder="Aggiungi operatore..."
                noOptionsMessage={() => 'Nessun dipendente'}
                options={availableExtraEmployees.map(e => ({ value: e.id, label: `${e.firstName} ${e.lastName}` }))}
                value={null}
                onChange={selected => selected && addOperator(selected.value)}
                styles={{ control: base => ({ ...base, minHeight: '26px', fontSize: '13px' }) }}
              />
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={sectionHeaderStyle}>Presenti</div>
          <div style={{ padding: '4px 8px' }}>
            {data.attendees.map((a, idx) => (
              <div key={idx} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px', fontSize: '13px', padding: '1px 0' }}>
                <input type="checkbox" checked={a.present} onChange={() => updateAttendee(idx, { present: !a.present })} style={{ width: '11px', height: '11px', flexShrink: 0 }} />
                <input
                  placeholder="Nome"
                  value={a.fullName}
                  onChange={e => updateAttendee(idx, { fullName: e.target.value })}
                  style={{ ...fieldInputStyle, flex: '1 1 100px', fontSize: '13px' }}
                />
                <input
                  placeholder="Nota"
                  value={a.note || ''}
                  onChange={e => updateAttendee(idx, { note: e.target.value })}
                  style={{ ...fieldInputStyle, flex: '1 1 100px', fontSize: '13px' }}
                />
                <button type="button" onClick={() => removeAttendee(idx)} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '13px', flexShrink: 0 }}>✕</button>
              </div>
            ))}
            <button type="button" onClick={addAttendee} style={{ fontSize: '13px', border: '1px solid #94a3b8', background: 'white', cursor: 'pointer', padding: '2px 8px', marginTop: '4px' }}>+ Aggiungi presente</button>
          </div>
        </div>
      </div>

      {/* Orario / Mezzi */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', borderBottom: '1px solid #94a3b8' }}>
        <div style={{ flex: 1, minWidth: 0, borderRight: isMobile ? 'none' : '1px solid #94a3b8', borderBottom: isMobile ? '1px solid #94a3b8' : 'none' }}>
          <div style={sectionHeaderStyle}>Ora inizio / fine lavoro</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '4px 8px' }}>
            <div style={{ flex: '1 1 100px', minWidth: 0 }}>
              <label style={fieldLabelStyle}>Inizio</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap' }}>{reportDate}</span>
                <input type="time" style={fieldInputStyle} value={data.startTime} onChange={e => field('startTime', e.target.value)} />
              </div>
            </div>
            <div style={{ flex: '1 1 100px', minWidth: 0 }}>
              <label style={fieldLabelStyle}>Fine</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap' }}>{reportDate}</span>
                <input type="time" style={fieldInputStyle} value={data.endTime} onChange={e => field('endTime', e.target.value)} />
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={sectionHeaderStyle}>Mezzi utilizzati</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', padding: '4px 8px' }}>
            {data.vehicles.map(v => (
              <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                <input type="checkbox" checked={v.selected} onChange={() => toggleVehicle(v.id)} style={{ width: '11px', height: '11px' }} />
                {v.name}{v.plate ? ` (${v.plate})` : ''}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Firme */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', borderBottom: '1px solid #94a3b8' }}>
        <div style={{ flex: 1, minWidth: 0, borderRight: isMobile ? 'none' : '1px solid #94a3b8', borderBottom: isMobile ? '1px solid #94a3b8' : 'none', padding: '6px 8px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Firma EGR ({compiledByLabel})</div>
          <div style={{ height: '80px', border: '1px dashed #94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {employeeSignatureUrl ? (
              <img src={employeeSignatureUrl} alt="Firma operatore" style={{ maxHeight: '76px', maxWidth: '100%' }} />
            ) : (
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>Firma non disponibile</span>
            )}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0, padding: '6px 8px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Firma cliente</div>
          <input
            placeholder="Nome e cognome firmatario"
            value={data.customerSignerName}
            onChange={e => field('customerSignerName', e.target.value)}
            style={{ ...fieldInputStyle, marginBottom: '4px' }}
          />
          {!readOnlyCustomerSignature && !data.customerSignerName.trim() && (
            <span style={{ fontSize: '11px', color: '#dc2626', display: 'block', marginTop: '-2px', marginBottom: '4px' }}>
              Il nome del firmatario è obbligatorio per inviare il rapporto
            </span>
          )}
          {readOnlyCustomerSignature ? (
            <div style={{ height: '80px', border: '1px dashed #94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {data.customerSignatureDataUrl ? (
                <img src={data.customerSignatureDataUrl} alt="Firma cliente" style={{ maxHeight: '76px', maxWidth: '100%' }} />
              ) : (
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>Firma non disponibile</span>
              )}
            </div>
          ) : (
            <>
              <ESignaturePad
                ref={customerSignatureRef}
                accentColor="#000000"
                onChange={({ isValid }) => setHasCustomerSignature(isValid)}
              />
              {!hasCustomerSignature && (
                <span style={{ fontSize: '11px', color: '#dc2626', display: 'block', marginTop: '4px' }}>
                  La firma del cliente è obbligatoria per inviare il rapporto
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Valutazione servizio */}
      <div>
        <div style={sectionHeaderStyle}>Valutazione servizio</div>
        <div style={{ fontSize: '12px', color: '#1a2942', padding: '4px 10px 0' }}>
          Valuti la nostra prestazione, ci aiuterà a migliorare i nostri servizi
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', padding: '4px 10px' }}>
          {SERVICE_RATING_OPTIONS.map(opt => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
              <input
                type="radio"
                name="serviceRating"
                checked={data.serviceRating === opt.value}
                onChange={() => field('serviceRating', opt.value)}
                style={{ width: '11px', height: '11px' }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', justifyContent: 'space-between', gap: isMobile ? '8px' : '4px 10px', padding: '6px 10px', borderTop: '1px solid #94a3b8', fontSize: '10.5px', color: '#64748b' }}>
        <div>
          <strong style={{ color: '#1a2942' }}>EGR SRL</strong><br />
          Via Grecia, 8 – 70032 Bitonto (BA) — P.IVA 08762090721<br />
          Mail: info@egrsrl.com — Pec: egrsrlbitonto@pec.it — Web: www.ecogrouprizzi.it
        </div>
        <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
          <strong style={{ color: '#1a2942' }}>ASSISTENZA H24</strong><br />
          080 2223343<br />
          Cell. 393 8789920 / 389 0018570
        </div>
      </div>

      {!hideActions && (
        <div className="no-print" style={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', gap: '10px', padding: '12px 10px' }}>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel} isDisabled={isSubmitting}>Annulla</Button>
          )}
          <Button
            backgroundColor="#688CC8"
            color="white"
            _hover={{ bg: 'gray.700' }}
            _active={{ bg: 'gray.600' }}
            _focus={{ boxShadow: 'none' }}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={!readOnlyCustomerSignature && (!hasCustomerSignature || !data.customerSignerName.trim())}
          >
            {submitLabel}
          </Button>
        </div>
      )}
    </div>
  );
});

InterventionReportForm.displayName = 'InterventionReportForm';

export default InterventionReportForm;
